import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// Public Website Pages
import PublicHomePage from "./pages/PublicHomePage";
import DriverHiringPage from "./pages/DriverHiringPage";
import PublicApplicationPage from "./pages/PublicApplicationPage";
import FAQPage from "./pages/FAQPage";
import PoliciesPage from "./pages/PoliciesPage";
import ContactPage from "./pages/ContactPage";
import AboutPage from "./pages/AboutPage";
import ProgramsPage from "./pages/ProgramsPage";
import LeaseOnPage from "./pages/LeaseOnPage";
import CompanyDriverPage from "./pages/CompanyDriverPage";
import DispatchServicesPage from "./pages/DispatchServicesPage";
import TrackApplicationPage from "./pages/TrackApplicationPage";
import PublicQuotePage from "./pages/PublicQuotePage";

// Portal Pages (Legacy - keeping for internal routing)
import CareersPage from "./pages/CareersPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ApplicantDashboard from "./pages/ApplicantDashboard";
import HomeRouter from "./pages/HomeRouter";
import ApplicationFormPage from "./pages/ApplicationFormPage";
import AdminDashboard from "./pages/AdminDashboard";
import SetPasswordPage from "./pages/SetPasswordPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";

// Phase 4 Pages
import FeatureTogglesPage from "./pages/FeatureTogglesPage";
import DispatchDashboard from "./pages/DispatchDashboard";
import LoadManagement from "./pages/LoadManagement";
import OperationalHistory from "./pages/OperationalHistory";
import CapabilityCheck from "./pages/CapabilityCheck";
import TrackingDashboard from "./pages/TrackingDashboard";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import BrokerPortal from "./pages/BrokerPortal";

// Phase 5 Pages
import StaffManagement from "./pages/StaffManagement";
import MaintenanceDashboard from "./pages/MaintenanceDashboard";
import AccountingDashboardPage from "./pages/AccountingDashboard";

// Onboarding Pages
import OnboardingQueue from "./pages/OnboardingQueue";
import OnboardingDetails from "./pages/OnboardingDetails";
import PeopleDirectoryPage from "./pages/PeopleDirectoryPage";
import FounderAttentionHome from "./pages/FounderAttentionHome";
import PersonFilePage from "./pages/PersonFilePage";
import CompanyFilePage from "./pages/CompanyFilePage";
import Customer360Page from "./pages/Customer360Page";
import DriverOnboarding from "./pages/DriverOnboarding";

// Compensation Policy Pages
import DriverPayExplainerPage from "./pages/DriverPayExplainerPage";

// Applicant Tracking
import ApplicantTrackingDashboard from "./pages/ApplicantTrackingDashboard";
import OperationsDashboard from "./pages/OperationsDashboard";
import RecruiterQueuePage from "./pages/RecruiterQueuePage";
import RecruiterWorkspace from "./pages/RecruiterWorkspace";

// Job Postings (Internal Draft Management)
import AdminJobsPage from "./pages/AdminJobsPage";

// Account Settings
import AccountSettingsPage from "./pages/AccountSettingsPage";
import CommunicationPreferencesPage from "./pages/CommunicationPreferencesPage";
import DriverMessagesPage from "./pages/DriverMessagesPage";
import DriverDocumentCenterPage from "./pages/DriverDocumentCenterPage";
import AdminDocumentCenterPage from "./pages/AdminDocumentCenterPage";
import AdminLeadsPage from "./pages/AdminLeadsPage";
import AdminQuoteBuilderPage from "./pages/AdminQuoteBuilderPage";
import PartnerActivatePage from "./pages/PartnerActivatePage";
import PartnerPortalPage from "./pages/PartnerPortalPage";
import PartnerManagementPage from "./pages/PartnerManagementPage";
import PartnerRequestsPage from "./pages/PartnerRequestsPage";
import IntegrationConsole from "./pages/IntegrationConsole";
import SalesIntelligencePage from "./pages/SalesIntelligencePage";
import ActivitiesPage from "./pages/ActivitiesPage";
import ActivationStatusPage from "./pages/ActivationStatusPage";
import MyDocumentsPage from "./pages/MyDocumentsPage";
import DriverWorkspacePage from "./pages/DriverWorkspacePage";
import WorkflowAutomationPage from "./pages/WorkflowAutomationPage";
import AIOpsPage from "./pages/AIOpsPage";
import IntelligentNotificationsPage from "./pages/IntelligentNotificationsPage";
import PredictiveCompliancePage from "./pages/PredictiveCompliancePage";
import EnterpriseIntegrationsPage from "./pages/EnterpriseIntegrationsPage";
import DriverContactUpdatePage from "./pages/DriverContactUpdatePage";

// Staff Administration
import AuditLogsPage from "./pages/AuditLogsPage";
import StaffOnboardingPage from "./pages/StaffOnboardingPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import PasswordResetHistoryPage from "./pages/PasswordResetHistoryPage";
import UserActivitySummaryPage from "./pages/UserActivitySummaryPage";
import StaffDirectoryPage from "./pages/StaffDirectoryPage";
import AllUsersPage from "./pages/AllUsersPage";

// Site Content Editor (Mini CMS - Super Admin Only)
import SiteContentEditorPage from "./pages/SiteContentEditorPage";
import CommunicationsCenterPage from "./pages/CommunicationsCenterPage";

// Phase 6 - Future Modules (Scaffolded, Disabled by Default)
import DispatchAutomationPage from "./pages/DispatchAutomationPage";
import LoadBoardsPage from "./pages/LoadBoardsPage";
import PayrollIntegrationPage from "./pages/PayrollIntegrationPage";
import AdvancedAnalyticsPage from "./pages/AdvancedAnalyticsPage";
import DriverDashboardPage from "./pages/DriverDashboardPage";
import BlueLockHubPage from "./pages/BlueLockHubPage";
import FounderMaintenancePage from "./pages/FounderMaintenancePage";

// Security Components
import MFAEnforcementWrapper from "./components/MFAEnforcementWrapper";
import ForcePasswordChangeWrapper from "./components/ForcePasswordChangeWrapper";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, superAdminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Super Admin only routes - also allow ops_admin (Partner) for dashboard visibility
  if (superAdminOnly && user.role !== 'super_admin' && user.role !== 'ops_admin') {
    return <Navigate to="/admin/dashboard" />;
  }

  if (adminOnly) {
    const isAdmin = ['admin', 'super_admin', 'safety_compliance_admin', 'payroll_admin', 'ops_admin'].includes(user.role);
    if (!isAdmin) {
      return <Navigate to="/dashboard" />;
    }
  }

  // Wrap with MFA enforcement for admin routes
  // Super Admin MUST have MFA enabled (except for /settings page)
  if (adminOnly || superAdminOnly) {
    return <MFAEnforcementWrapper>{children}</MFAEnforcementWrapper>;
  }

  return children;
};

// Partner Route Component — external PARTNER_USER only (no MFA wrapper, no admin nav)
const PartnerRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-gray-500">Loading...</div></div>;
  }
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'partner_user') return <Navigate to="/dashboard" />;
  return children;
};

// Public Route Component (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (user) {
    // All authenticated users land on /dashboard; HomeRouter renders the role-aware home
    // (Founder Command Center for admins/staff, ApplicantDashboard for drivers/applicants).
    return <Navigate to="/dashboard" />;
  }

  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ForcePasswordChangeWrapper>
            <Routes>
            {/* Public Website Routes */}
            <Route path="/" element={<PublicHomePage />} />
            <Route path="/drivers" element={<DriverHiringPage />} />
            <Route path="/apply" element={<PublicApplicationPage />} />
            <Route path="/track-application" element={<TrackApplicationPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/policies" element={<PoliciesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/programs" element={<ProgramsPage />} />
            <Route path="/lease-on" element={<LeaseOnPage />} />
            <Route path="/company-driver" element={<CompanyDriverPage />} />
            <Route path="/dispatch-services" element={<DispatchServicesPage />} />
            <Route path="/careers" element={<CareersPage />} />
            
            {/* Public Token-Secured Onboarding */}
            <Route path="/driver-onboarding/:token" element={<DriverOnboarding />} />

            {/* Public Token-Secured Customer Quote Portal (Slice 4) */}
            <Route path="/quote/:token" element={<PublicQuotePage />} />

            {/* Partner Portal — public activation + external partner app */}
            <Route path="/partner/activate" element={<PartnerActivatePage />} />
            <Route path="/partner" element={<PartnerRoute><PartnerPortalPage /></PartnerRoute>} />
            
            {/* Auth Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <RegisterPage />
                </PublicRoute>
              }
            />
            
            {/* Password Management Routes */}
            <Route path="/set-password" element={<SetPasswordPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              }
            />

            {/* Protected User Routes */}
            <Route
              path="/hub"
              element={
                <ProtectedRoute>
                  <BlueLockHubPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <HomeRouter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/application/new"
              element={
                <ProtectedRoute>
                  <ApplicationFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/application/edit"
              element={
                <ProtectedRoute>
                  <ApplicationFormPage />
                </ProtectedRoute>
              }
            />

            {/* Protected Admin Routes */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Applicant Tracking Dashboard */}
            <Route
              path="/applicants"
              element={
                <ProtectedRoute adminOnly={true}>
                  <ApplicantTrackingDashboard />
                </ProtectedRoute>
              }
            />

            {/* Operations Intelligence Dashboard (Neo vNext M2 — read-only) */}
            <Route
              path="/operations"
              element={
                <ProtectedRoute adminOnly={true}>
                  <OperationsDashboard />
                </ProtectedRoute>
              }
            />

            {/* Recruiter Queue (Neo vNext M2 — read-only) */}
            <Route
              path="/recruiter-queue"
              element={
                <ProtectedRoute adminOnly={true}>
                  <RecruiterQueuePage />
                </ProtectedRoute>
              }
            />

            {/* Recruiter Workspace (Neo vNext M2 — recruiter work actions) */}
            <Route
              path="/recruiter-workspace"
              element={
                <ProtectedRoute adminOnly={true}>
                  <RecruiterWorkspace />
                </ProtectedRoute>
              }
            />
            
            {/* Onboarding Routes (Admin/Dispatcher) */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute adminOnly={true}>
                  <OnboardingQueue />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/:id"
              element={
                <ProtectedRoute adminOnly={true}>
                  <OnboardingDetails />
                </ProtectedRoute>
              }
            />

            {/* Person File & Company File (CRM) */}
            <Route
              path="/admin/attention"
              element={
                <ProtectedRoute adminOnly={true}>
                  <FounderAttentionHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/people"
              element={
                <ProtectedRoute adminOnly={true}>
                  <PeopleDirectoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/person/:id"
              element={
                <ProtectedRoute adminOnly={true}>
                  <PersonFilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/company/:id"
              element={
                <ProtectedRoute adminOnly={true}>
                  <CompanyFilePage />
                </ProtectedRoute>
              }
            />
            
            {/* Phase 4 Admin Routes */}
            <Route
              path="/admin/feature-toggles"
              element={
                <ProtectedRoute adminOnly={true}>
                  <FeatureTogglesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/dispatch"
              element={
                <ProtectedRoute adminOnly={true}>
                  <DispatchDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/loads"
              element={
                <ProtectedRoute adminOnly={true}>
                  <LoadManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/history"
              element={
                <ProtectedRoute adminOnly={true}>
                  <OperationalHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/capability-check"
              element={
                <ProtectedRoute adminOnly={true}>
                  <CapabilityCheck />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/tracking"
              element={
                <ProtectedRoute adminOnly={true}>
                  <TrackingDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AnalyticsDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/broker"
              element={
                <ProtectedRoute adminOnly={true}>
                  <BrokerPortal />
                </ProtectedRoute>
              }
            />
            
            {/* Phase 5 Admin Routes */}
            <Route
              path="/admin/staff"
              element={
                <ProtectedRoute adminOnly={true}>
                  <StaffManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/maintenance"
              element={
                <ProtectedRoute adminOnly={true}>
                  <MaintenanceDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/accounting"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AccountingDashboardPage />
                </ProtectedRoute>
              }
            />
            
            {/* Job Postings (Internal Draft Management - Admin view, Super Admin edit) */}
            <Route
              path="/admin/jobs"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminJobsPage />
                </ProtectedRoute>
              }
            />

            {/* Account Settings (All staff roles) */}
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <AccountSettingsPage />
                </ProtectedRoute>
              }
            />

            {/* Admin Document Center — extends existing /documents/* infra (S3 + RBAC + audit) */}
            <Route
              path="/admin/communications"
              element={
                <ProtectedRoute adminOnly={true}>
                  <CommunicationsCenterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/documents"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminDocumentCenterPage />
                </ProtectedRoute>
              }
            />

            {/* Admin Leads — Quote & Lead Management (flag-gated quote_lead_enabled) */}
            <Route
              path="/admin/leads"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminLeadsPage />
                </ProtectedRoute>
              }
            />

            {/* Admin Quote Builder — Quote & Lead Management (flag-gated quote_builder_enabled) */}
            <Route
              path="/admin/quote-builder"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminQuoteBuilderPage />
                </ProtectedRoute>
              }
            />

            {/* Partner Portal — Founder/admin management + request handoff */}
            <Route path="/admin/partners" element={<ProtectedRoute adminOnly={true}><PartnerManagementPage /></ProtectedRoute>} />
            <Route path="/admin/partner-requests" element={<ProtectedRoute adminOnly={true}><PartnerRequestsPage /></ProtectedRoute>} />
            <Route path="/admin/partner-requests/:id" element={<ProtectedRoute adminOnly={true}><PartnerRequestsPage /></ProtectedRoute>} />

            {/* Controlled Internal Integration Phase — Track 1 (flag-gated relationship_engine_enabled) */}
            <Route
              path="/admin/integration"
              element={
                <ProtectedRoute adminOnly={true}>
                  <IntegrationConsole />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/customer-360"
              element={
                <ProtectedRoute adminOnly={true}>
                  <Customer360Page />
                </ProtectedRoute>
              }
            />

            {/* Revenue & Sales Intelligence — Slice 5 (flag-gated quote_reporting_enabled) */}
            <Route
              path="/admin/sales-intelligence"
              element={
                <ProtectedRoute adminOnly={true}>
                  <SalesIntelligencePage />
                </ProtectedRoute>
              }
            />

            {/* Follow-Up Activities — Slice 6 (flag-gated follow_up_activities_enabled) */}
            <Route
              path="/admin/activities"
              element={
                <ProtectedRoute adminOnly={true}>
                  <ActivitiesPage />
                </ProtectedRoute>
              }
            />

            {/* Activation Status — Soft Go-Live observability (read-only, admin) */}
            <Route
              path="/admin/activation-status"
              element={
                <ProtectedRoute adminOnly={true}>
                  <ActivationStatusPage />
                </ProtectedRoute>
              }
            />


            {/* Driver My Documents — same backend infra, owner-scoped */}
            <Route
              path="/my-documents"
              element={
                <ProtectedRoute>
                  <MyDocumentsPage />
                </ProtectedRoute>
              }
            />

            {/* Communication Preferences (M3 F2 — flag-gated, owner-scoped) */}
            <Route
              path="/communication-preferences"
              element={
                <ProtectedRoute>
                  <CommunicationPreferencesPage />
                </ProtectedRoute>
              }
            />

            {/* Driver Messages / Notifications Center (M3 F4 — flag-gated, owner-scoped, read-only) */}
            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <DriverMessagesPage />
                </ProtectedRoute>
              }
            />

            {/* Driver Document Center (M3 F5 — flag-gated, owner-scoped, read aggregation) */}
            <Route
              path="/documents-center"
              element={
                <ProtectedRoute>
                  <DriverDocumentCenterPage />
                </ProtectedRoute>
              }
            />

            {/* Driver Workspace (M3 F6 — flag-gated, owner-scoped, read aggregation) */}
            <Route
              path="/workspace"
              element={
                <ProtectedRoute>
                  <DriverWorkspacePage />
                </ProtectedRoute>
              }
            />

            {/* Workflow Automation (M4 F1 — flag-gated, Super Admin, rules-engine substrate) */}
            <Route
              path="/admin/automation"
              element={
                <ProtectedRoute adminOnly={true}>
                  <WorkflowAutomationPage />
                </ProtectedRoute>
              }
            />

            {/* AI-Assisted Operations (M4 F2 — flag-gated, advisory recommendations) */}
            <Route
              path="/admin/ai-ops"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AIOpsPage />
                </ProtectedRoute>
              }
            />

            {/* Intelligent Notifications (M4 F3 — flag-gated, advisory + human dispatch) */}
            <Route
              path="/admin/intelligent-notifications"
              element={
                <ProtectedRoute adminOnly={true}>
                  <IntelligentNotificationsPage />
                </ProtectedRoute>
              }
            />

            {/* Predictive Compliance (M4 F4 — flag-gated, read-only advisory predictions) */}
            <Route
              path="/admin/predictive-compliance"
              element={
                <ProtectedRoute adminOnly={true}>
                  <PredictiveCompliancePage />
                </ProtectedRoute>
              }
            />

            {/* Future Enterprise Integrations (M4 F5 — flag-gated, MOCKED connector registry) */}
            <Route
              path="/admin/enterprise-integrations"
              element={
                <ProtectedRoute adminOnly={true}>
                  <EnterpriseIntegrationsPage />
                </ProtectedRoute>
              }
            />

            {/* Driver Contact Update (Token-protected, public) */}
            <Route path="/update-contact/:token" element={<DriverContactUpdatePage />} />

            {/* Audit Logs (Super Admin full, Admin read-only) */}
            <Route
              path="/admin/audit-logs"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AuditLogsPage />
                </ProtectedRoute>
              }
            />

            {/* All Users Directory - Unified view (Admin/Super Admin) */}
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AllUsersPage />
                </ProtectedRoute>
              }
            />

            {/* Password Reset History (Super Admin Only) */}
            <Route
              path="/admin/password-reset-history"
              element={
                <ProtectedRoute superAdminOnly={true}>
                  <PasswordResetHistoryPage />
                </ProtectedRoute>
              }
            />

            {/* Staff Onboarding Checklist (Admin/Super Admin) */}
            <Route
              path="/admin/staff-onboarding"
              element={
                <ProtectedRoute adminOnly={true}>
                  <StaffOnboardingPage />
                </ProtectedRoute>
              }
            />

            {/* Site Content Editor (Super Admin Only - Mini CMS) */}
            <Route
              path="/admin/site-content"
              element={
                <ProtectedRoute superAdminOnly={true}>
                  <SiteContentEditorPage />
                </ProtectedRoute>
              }
            />

            {/* User Activity Summary (Admin/Super Admin - Compliance Auditing) */}
            <Route
              path="/admin/user-activity"
              element={
                <ProtectedRoute adminOnly={true}>
                  <UserActivitySummaryPage />
                </ProtectedRoute>
              }
            />

            {/* Staff Directory (Admin/Super Admin - Suspension Management) */}
            <Route
              path="/admin/staff-directory"
              element={
                <ProtectedRoute adminOnly={true}>
                  <StaffDirectoryPage />
                </ProtectedRoute>
              }
            />
            {/* ============================================ */}
            {/* Phase 6 - Future Modules (Scaffolded, Feature-Flagged) */}
            {/* All modules are DISABLED by default */}
            {/* ============================================ */}

            {/* Dispatch Automation (Admin/Super Admin) */}
            <Route
              path="/dispatch-automation"
              element={
                <ProtectedRoute adminOnly={true}>
                  <DispatchAutomationPage />
                </ProtectedRoute>
              }
            />

            {/* Load Boards (Admin/Super Admin) */}
            <Route
              path="/load-boards"
              element={
                <ProtectedRoute adminOnly={true}>
                  <LoadBoardsPage />
                </ProtectedRoute>
              }
            />

            {/* Payroll / Settlements Integration (Super Admin only) */}
            <Route
              path="/payroll"
              element={
                <ProtectedRoute superAdminOnly={true}>
                  <PayrollIntegrationPage />
                </ProtectedRoute>
              }
            />

            {/* Advanced Analytics (Admin/Super Admin) */}
            <Route
              path="/advanced-analytics"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdvancedAnalyticsPage />
                </ProtectedRoute>
              }
            />

            {/* Driver Self-Service Dashboard (Driver role when enabled) */}
            <Route
              path="/driver-dashboard"
              element={
                <ProtectedRoute>
                  <DriverDashboardPage />
                </ProtectedRoute>
              }
            />

            {/* Driver Pay Explainer - Hidden from navigation, linked from onboarding */}
            <Route
              path="/driver/pay-explainer"
              element={
                <ProtectedRoute>
                  <DriverPayExplainerPage />
                </ProtectedRoute>
              }
            />

            {/* Founder Maintenance (Super Admin only — production data registration + fixture cleanup) */}
            <Route
              path="/admin/founder-maintenance"
              element={
                <ProtectedRoute superAdminOnly={true}>
                  <FounderMaintenancePage />
                </ProtectedRoute>
              }
            />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          </ForcePasswordChangeWrapper>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
